// src/sectors/pilates/settings/tabs/sections/company/ActivitiesSection.jsx
import React from 'react';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import { activitiesApi } from '../../activities/activitiesApi';
import { ConfirmDialog } from 'sectors/pilates/sessions/modal/parts/ConfirmDialog';

function toInt(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

export function CompanyActivitiesSection({ loading }) {
  const theme = useMuiTheme();

  // ---- shared compact tokens (match packages) ----
  const GAP = 1;
  const TITLE_SX = { fontWeight: 900, fontSize: 13, lineHeight: 1.1 };
  const SUB_SX = { opacity: 0.8, fontWeight: 800, fontSize: 12 };
  const ERR_SX = { color: 'error.main', fontWeight: 900 };
  const softDialogPaper = React.useMemo(
    () => ({
      borderRadius: '20px',
      overflow: 'hidden',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[10],
      background:
        theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, rgba(26,28,36,.92), rgba(18,20,26,.96))'
          : 'linear-gradient(180deg, rgba(255,255,255,.96), rgba(250,250,252,.98))',
    }),
    [theme]
  );

  const rowSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '14px',
    p: 1,
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '1fr 120px' },
    gap: 1,
    alignItems: 'center',
    background:
      theme.palette.mode === 'dark'
        ? `linear-gradient(180deg, ${alpha('#0B1020', 0.35)}, ${alpha('#070A12', 0.22)})`
        : `linear-gradient(180deg, ${alpha('#ffffff', 0.92)}, ${alpha('#fbfbff', 0.9)})`,
  };

  const actionBtnSx = {
    borderRadius: '12px',
    px: 1.1,
    minHeight: 34,
    fontWeight: 850,
    textTransform: 'none',
  };

  const iconBtnSx = {
    p: 0.6,
    borderRadius: '12px',
  };

  const [items, setItems] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  // create
  const [createOpen, setCreateOpen] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newDur, setNewDur] = React.useState(50);

  // edit
  const [editOpen, setEditOpen] = React.useState(false);
  const [editId, setEditId] = React.useState(null);
  const [editName, setEditName] = React.useState('');
  const [editDur, setEditDur] = React.useState(50);

  const fetchList = React.useCallback(async () => {
    setErr(null);
    try {
      const res = await activitiesApi.list();
      const list = Array.isArray(res) ? res : Array.isArray(res?.items) ? res.items : [];
      setItems(list);
    } catch (e) {
      console.error(e);
      setErr('Aktiviteler yüklenemedi.');
    }
  }, []);

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openCreate = () => {
    setErr(null);
    setNewName('');
    setNewDur(50);
    setCreateOpen(true);
  };

  const openEdit = (a) => {
    setErr(null);
    setEditId(a?.id ?? null);
    setEditName(a?.name ?? '');
    setEditDur(a?.durationMin ?? 50);
    setEditOpen(true);
  };

  const closeCreate = () => {
    if (busy) return;
    setCreateOpen(false);
  };
  const closeEdit = () => {
    if (busy) return;
    setEditOpen(false);
    setEditId(null);
  };

  const createCanSave = String(newName || '').trim().length >= 2;

  const doCreate = async () => {
    if (!createCanSave || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const payload = { name: String(newName || '').trim() };
      payload.durationMin = Math.max(5, toInt(newDur, 50));
      await activitiesApi.create(payload);
      setCreateOpen(false);
      await fetchList();
    } catch (e) {
      console.error(e);
      setErr('Aktivite kaydedilemedi.');
    } finally {
      setBusy(false);
    }
  };

  const doUpdate = async () => {
    if (!editId || busy) return;
    const name = String(editName || '').trim();
    if (name.length < 2) return;

    setBusy(true);
    setErr(null);
    try {
      const payload = { name };
      payload.durationMin = Math.max(5, toInt(editDur, 50));
      await activitiesApi.update(editId, payload);
      setEditOpen(false);
      setEditId(null);
      await fetchList();
    } catch (e) {
      console.error(e);
      setErr('Aktivite güncellenemedi.');
    } finally {
      setBusy(false);
    }
  };

  const doRemove = async (id) => {
    if (!id || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await activitiesApi.remove(id);
      await fetchList();
    } catch (e) {
      console.error(e);
      setErr('Aktivite silinemedi.');
    } finally {
      setBusy(false);
    }
  };

  const list = Array.isArray(items) ? items : [];
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [pendingDelete, setPendingDelete] = React.useState(null);

  const showDur =
    editId != null && 'durationMin' in (list.find((x) => String(x.id) === String(editId)) || {});

  return (
    <MDBox sx={{ display: 'grid', gap: GAP }}>
      {/* Header row (match packages style) */}
      <MDBox display="flex" alignItems="center" justifyContent="space-between">
        <MDBox>
          <MDTypography sx={TITLE_SX}>Aktiviteler</MDTypography>
          <MDTypography sx={SUB_SX}>Ekle • Düzenle • Sil</MDTypography>
        </MDBox>

        <MDButton
          variant="outlined"
          color="info"
          sx={actionBtnSx}
          onClick={openCreate}
          disabled={loading || busy}
        >
          <Icon sx={{ mr: 0.5, fontSize: 18 }}>add</Icon>
          Aktivite
        </MDButton>
      </MDBox>

      <Divider sx={{ my: 0.5 }} />

      {err ? (
        <MDTypography variant="caption" sx={ERR_SX}>
          {err}
        </MDTypography>
      ) : null}

      {loading ? (
        <MDTypography variant="button" sx={{ opacity: 0.7, fontWeight: 800 }}>
          Yükleniyor...
        </MDTypography>
      ) : list.length === 0 ? (
        <MDTypography variant="button" sx={{ opacity: 0.7, fontWeight: 800 }}>
          Aktivite yok.
        </MDTypography>
      ) : (
        <MDBox sx={{ display: 'grid', gap: 0.9 }}>
          {list.map((a) => (
            <MDBox key={String(a.id)} sx={rowSx}>
              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography sx={{ fontWeight: 900, fontSize: 13, lineHeight: 1.15 }}>
                  {a?.name || '—'}
                </MDTypography>

                {'durationMin' in (a || {}) ? (
                  <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
                    {Number(a?.durationMin ?? 50)} dk
                  </MDTypography>
                ) : null}
              </MDBox>

              <MDBox display="flex" justifyContent="flex-end" gap={0.25}>
                <Tooltip title="Düzenle">
                  <IconButton sx={iconBtnSx} onClick={() => openEdit(a)} disabled={busy}>
                    <Icon sx={{ fontSize: 20, color: theme.palette.text.primary }}>edit</Icon>
                  </IconButton>
                </Tooltip>

                <Tooltip title="Sil">
                  <IconButton
                    sx={iconBtnSx}
                    onClick={() => {
                      setPendingDelete({ id: a.id, name: a?.name || 'Aktivite' });
                      setConfirmOpen(true);
                    }}
                    disabled={busy}
                  >
                    <Icon sx={{ fontSize: 20, color: theme.palette.error.main }}>delete</Icon>
                  </IconButton>
                </Tooltip>
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
      )}

      {/* CREATE */}
      <Dialog
        open={createOpen}
        onClose={closeCreate}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: softDialogPaper }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography sx={{ fontWeight: 900, fontSize: 14 }}>Aktivite Oluştur</MDTypography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <MDBox sx={{ display: 'grid', gap: 1.1, mt: 0.5 }}>
            <MDInput
              label="Aktivite adı"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />

            <MDInput
              label="Süre (dk)"
              type="number"
              value={newDur}
              onChange={(e) => setNewDur(e.target.value)}
            />

            <MDBox display="flex" justifyContent="flex-end" gap={1}>
              <MDButton variant="outlined" color="dark" onClick={closeCreate} disabled={busy}>
                Vazgeç
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                onClick={doCreate}
                disabled={!createCanSave || busy}
              >
                <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
                Kaydet
              </MDButton>
            </MDBox>
          </MDBox>
        </DialogContent>
      </Dialog>

      {/* EDIT */}
      <Dialog
        open={editOpen}
        onClose={closeEdit}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: softDialogPaper }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography sx={{ fontWeight: 900, fontSize: 14 }}>Aktivite Düzenle</MDTypography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <MDBox sx={{ display: 'grid', gap: 1.1, mt: 0.5 }}>
            <MDInput
              label="Aktivite adı"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />

            {showDur ? (
              <MDInput
                label="Süre (dk)"
                type="number"
                value={editDur}
                onChange={(e) => setEditDur(e.target.value)}
              />
            ) : null}

            <MDBox display="flex" justifyContent="flex-end" gap={1}>
              <MDButton variant="outlined" color="dark" onClick={closeEdit} disabled={busy}>
                Vazgeç
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                onClick={doUpdate}
                disabled={busy || String(editName || '').trim().length < 2 || !editId}
              >
                <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
                Kaydet
              </MDButton>
            </MDBox>
          </MDBox>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        tone="error"
        title="Aktivite Silinsin mi?"
        subtitle="Bu işlem geri alınamaz."
        message={() =>
          `“${pendingDelete?.name || 'Aktivite'}” silinecek.\nDevam etmek istiyor musun?`
        }
        confirmText="Sil"
        cancelText="Vazgeç"
        busy={busy}
        onCancel={() => {
          if (busy) return;
          setConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={async () => {
          if (!pendingDelete?.id) return;
          await doRemove(pendingDelete.id);
          setConfirmOpen(false);
          setPendingDelete(null);
        }}
      />
    </MDBox>
  );
}
